import { Github, Plug } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const ghIcon = <Github className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

function scalarFields(record: Record<string, unknown>) {
  return Object.entries(record)
    .filter(([key, value]) => key !== 'id' && formatToolValue(value))
    .slice(0, 24)
    .map(([key, value]) => ({
      label: key,
      value: formatToolValue(value),
      tone: normalizeTone(value),
    }))
}

function sectionContent(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([, entryValue]) => formatToolValue(entryValue)
  )
  if (entries.length === 0) return null
  return entries
    .map(([key, entryValue]) => `**${key}:** ${formatToolValue(entryValue)}`)
    .join('\n\n')
}

export function IntegrationsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const integrations: Array<Record<string, unknown>> = Array.isArray(
    state.result?.integrations
  )
    ? state.result.integrations
    : []
  const count = Array.isArray(state.result?.integrations)
    ? integrations.length
    : null
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading integrations...'
        completedLabel={
          count !== null
            ? `${plural(count, 'integration')} loaded`
            : 'Integrations loaded'
        }
        completedIcon={ghIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          columns={['Name', 'Type', 'Status', 'Category']}
          emptyLabel='No integrations found'
          items={integrations.map((integration, index) => {
            const title =
              pickString(integration.name, integration.type) ??
              'Untitled integration'
            return {
              id: null,
              title,
              subtitle: pickString(integration.type),
              meta: [
                formatToolValue(integration.status),
                formatToolValue(integration.category),
                integration.hasErrors ? 'errors' : null,
              ],
              tone: normalizeTone(
                integration.hasErrors ? 'error' : integration.status
              ),
              detailOverlay: {
                id: getToolCallId(part, `integration-${index}-${title}`),
                payload: {
                  entityType: 'integration',
                  toolName: 'integrationsList',
                  title,
                  description: pickString(integration.type),
                  fields: scalarFields(integration),
                  sections: [
                    {
                      title: 'Config',
                      content: sectionContent(integration.config),
                    },
                  ],
                },
              },
            }
          })}
        />
      ) : null}
    </>
  )
}

export function IntegrationsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const title =
    pickString(state.result?.name, state.result?.type, state.args?.integrationId) ??
    'Integration'
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading integration...'
        completedLabel='Integration loaded'
        loadingDetail={pickString(state.args?.integrationId)}
        completedDetail={title}
        completedIcon={ghIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(part, `integration-${title}`)}
          payload={{
            entityType: 'integration',
            toolName: 'integrationsGet',
            title,
            description: pickString(state.result.type),
            fields: scalarFields(state.result),
            sections: [
              { title: 'Config', content: sectionContent(state.result.config) },
              {
                title: 'Monitoring',
                content: sectionContent(state.result.monitoring),
              },
              { title: 'Health', content: sectionContent(state.result.health) },
              {
                title: 'Connections',
                content: Array.isArray(state.result.connections)
                  ? state.result.connections
                      .map(
                        (connection: Record<string, unknown>) =>
                          `**${pickString(connection.name, connection.connectionType) ?? 'Connection'}:** ${pickString(connection.status) ?? 'unknown'}`
                      )
                      .join('\n\n')
                  : null,
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function IntegrationsGetRepoSettingsToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const repoName =
    pickString(
      state.result?.repository,
      state.result?.repo,
      state.args?.repo
    ) ?? 'Repository'
  const owner = pickString(state.result?.owner, state.args?.owner)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading repo settings...'
        completedLabel='Repo settings loaded'
        loadingDetail={pickString(state.args?.repo)}
        completedDetail={owner ? `${owner}/${repoName}` : repoName}
        completedIcon={ghIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(part, `repo-settings-${owner ?? ''}-${repoName}`)}
          payload={{
            entityType: 'integration',
            toolName: 'integrationsGetRepoSettings',
            title: owner ? `${owner}/${repoName}` : repoName,
            description: 'Repository security settings',
            fields: scalarFields(state.result),
            sections: [
              {
                title: 'Repository settings',
                content: sectionContent(state.result),
              },
            ],
          }}
        />
      ) : null}
    </>
  )
}

export function IntegrationsRequestConnectToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const actions: Array<Record<string, unknown>> = Array.isArray(
    state.result?.connectActions
  )
    ? state.result.connectActions
    : []
  const skipped: Array<Record<string, unknown>> = Array.isArray(
    state.result?.skipped
  )
    ? state.result.skipped
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Requesting connection...'
        completedLabel='Connection requested'
        loadingDetail={
          Array.isArray(state.args?.integrationTypes)
            ? state.args.integrationTypes.join(', ')
            : pickString(state.args?.provider)
        }
        completedDetail={
          actions.length > 0
            ? `${plural(actions.length, 'connect option')}`
            : skipped.length > 0
              ? `${plural(skipped.length, 'integration')} skipped`
              : null
        }
        completedIcon={
          <Plug className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
        }
      />
      {state.isComplete && !isToolError(state) && skipped.length > 0 ? (
        <ToolListPreview
          columns={['Name', 'Reason', 'Status']}
          emptyLabel='No skipped integrations'
          items={skipped.map((item) => ({
            id: null,
            title: pickString(item.integrationType) ?? 'Integration',
            subtitle: pickString(item.reason),
            meta: ['skipped'],
            tone: 'medium',
          }))}
        />
      ) : null}
    </>
  )
}
